import { Typography } from "@mui/material";
import { useArrayState } from "@shared/frontend/hooks/use-array-state.hook";
import {
  getStoredValue,
  useSaveToStorage,
} from "@shared/frontend/hooks/use-storage.hook";
import { getChildEntity, isRelation } from "@shared/helpers/sql-relations";
import React, { useEffect, useMemo, useRef, useState } from "react";
import type { ClassType, EntityFilter, FieldMetadata } from "remult";
import { repo, SqlDatabase } from "remult";
import { getRelationFieldInfo } from "remult/internals";
import { Column as ColumnComponent } from "./Column";
import { HyperTable } from "./HyperTable";
import { ActionsBar } from "./inner-components/hyper-table-actions-bar/ActionsBar";
import HyperTableExpandColumn from "./inner-components/hyper-table-expand-column/HyperTableExpandColumn";
import HyperTableMenuActionsColumn from "./inner-components/hyper-table-menu-actions-column/HyperTableMenuActionsColumn";
import { HyperTableSelectRowColumn } from "./inner-components/hyper-table-select-row-column/HyperTableSelectRowColumn";
import { HyperTableCustomFilter } from "./inner-components/hyper-table-toolbar/HyperTableCustomFilter";
import { HyperTableEntityFilter } from "./inner-components/hyper-table-toolbar/HyperTableEntityFilter";
import { HyperTableToolbar } from "./inner-components/hyper-table-toolbar/HyperTableToolbar";
import { HyperTableHeader } from "./inner-components/HyperTableHeader";
import { NonEntityColumn } from "./NonEntityColumn";
import type {
  ActiveFilter,
  BuildEntityColumnConfig,
  BuildNonEntityColumnConfig,
  Column,
  FieldId,
  FieldsProxy,
  HyperTableContextValue,
  HyperTableDef,
  HyperTableDefMethod,
  HyperTableDefValues,
  HyperTableProps,
  Include,
  Obj,
} from "./types";
import { useHyperTableContext } from "./useHyperTableContext";

function DefaultCell(props: { infoToShow: string | number }) {
  const { infoToShow } = props;
  return (
    <Typography
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        height: "100%",
        padding: "1rem",
        textAlign: "start",
        fontSize: "0.9rem",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
      component="span"
      title={infoToShow.toString()}
    >
      {infoToShow.toString()} {/* Ensure that the value is a string */}
    </Typography>
  );
}

export function getRowId<Entity>(
  entity: ClassType<Entity>,
  row: Entity,
): string {
  return repo(entity).metadata.idMetadata.getId(row);
}

export function createFieldProxyForEntity<Entity>(
  entity: ClassType<Entity>,
  path?: FieldId<Entity>,
) {
  return new Proxy(
    {},
    {
      get(_, prop) {
        if (typeof prop === "symbol") return undefined;
        if (isRelation(entity, prop as keyof Entity & string)) {
          return createFieldProxyForEntity(
            getChildEntity(entity, prop as keyof Entity & string),
            (path ? `${path}.${prop}` : String(prop)) as FieldId<Entity>,
          );
        } else return path ? `${path}.${prop}` : prop;
      },
    },
  ) as FieldsProxy<Entity>;
}

export const DEFAULT_COLUMN_SIZE = 120;
export const COLUMN_RESIZE_MIN_SIZE = 40;
export const COLUMN_RESIZE_MAX_SIZE = 5000;

function resolveColumnResizeLimits(config: {
  minSize?: number;
  maxSize?: number;
  size?: number;
  enableResizing?: boolean;
}) {
  const { minSize, maxSize, size, enableResizing } = config;

  if (enableResizing === false) {
    return { minSize, maxSize, size };
  }

  const isFixedWidth =
    minSize != null && maxSize != null && minSize === maxSize;
  if (isFixedWidth) {
    return {
      minSize,
      maxSize,
      size: size ?? minSize,
    };
  }

  return {
    minSize: COLUMN_RESIZE_MIN_SIZE,
    maxSize: maxSize ?? COLUMN_RESIZE_MAX_SIZE,
    size: size ?? minSize ?? DEFAULT_COLUMN_SIZE,
  };
}

export type ColumnSizeInfo = {
  id: string;
  size?: number;
  minSize?: number;
  maxSize?: number;
  enableResizing?: boolean;
};

function isFixedWidthColumn(col: ColumnSizeInfo) {
  if (col.enableResizing === false) return true;
  return col.minSize != null && col.maxSize != null && col.minSize === col.maxSize;
}

function clampColumnSize(col: ColumnSizeInfo, raw: number) {
  const min = col.minSize ?? COLUMN_RESIZE_MIN_SIZE;
  const max = col.maxSize ?? COLUMN_RESIZE_MAX_SIZE;
  return Math.min(max, Math.max(min, raw));
}

export function fillColumnSizing(
  sizing: Record<string, number>,
  columns: ColumnSizeInfo[],
  containerWidth: number,
  lockedId?: string,
) {
  if (containerWidth <= 0 || columns.length === 0) return sizing;

  const next: Record<string, number> = {};
  for (const col of columns) {
    next[col.id] = clampColumnSize(
      col,
      sizing[col.id] ?? col.size ?? DEFAULT_COLUMN_SIZE,
    );
  }

  const currentTotal = columns.reduce((sum, col) => sum + next[col.id], 0);
  if (currentTotal >= containerWidth) return next;

  const flexCols = columns.filter(
    (col) => !isFixedWidthColumn(col) && col.id !== lockedId,
  );
  if (flexCols.length === 0) return next;

  const extra = containerWidth - currentTotal;
  const currentFlexSum =
    flexCols.reduce((sum, col) => sum + next[col.id], 0) || 1;

  let used = 0;
  const computed = flexCols.map((col) => {
    const size = clampColumnSize(
      col,
      next[col.id] + extra * (next[col.id] / currentFlexSum),
    );
    used += size - next[col.id];
    return size;
  });

  let remainder = extra - used;
  for (let i = flexCols.length - 1; i >= 0 && Math.abs(remainder) >= 0.5; i--) {
    const col = flexCols[i];
    const current = computed[i];
    if (!col || current == null) continue;
    const adjusted = clampColumnSize(col, current + remainder);
    remainder -= adjusted - current;
    computed[i] = adjusted;
  }

  flexCols.forEach((col, i) => {
    next[col.id] = Math.round(computed[i] ?? next[col.id]);
  });

  return next;
}

export function buildNonEntityColumn<Entity>(
  config: BuildNonEntityColumnConfig<Entity>,
): Column<Entity> {
  const {
    caption,
    id,
    meta,
    valueConverter,
    minSize,
    maxSize,
    size,
    enableResizing,
    header: headerOverride,
    ...rest
  } = config;
  return {
    id,
    accessorKey: id,
    include: {},
    enableSorting: true,
    enableResizing,
    ...resolveColumnResizeLimits({ minSize, maxSize, size, enableResizing }),
    cell: valueConverter
      ? (i) => <DefaultCell infoToShow={valueConverter(i.row.original, i)} />
      : undefined,
    meta: {
      caption,
      ...meta,
    },
    ...rest,
    header:
      typeof headerOverride === "function"
        ? headerOverride
        : (headerContext) => (
            <HyperTableHeader headerContext={headerContext} caption={caption} />
          ),
  };
}

export function buildEntityColumn<Entity>(
  ctx: HyperTableContextValue<Entity>,
  config: BuildEntityColumnConfig<Entity>,
): Column<Entity> {
  const {
    caption,
    field,
    valueConverter,
    minSize,
    maxSize,
    size,
    enableResizing,
    ...override
  } = config;
  const fieldSet = field.split(".");

  const finalField = fieldSet.pop()!;

  const include = fieldSet.reduceRight(
    (acc, key) => ({
      [key]: Object.keys(acc).length === 0 ? true : { include: acc },
    }),
    {},
  );

  const finalEntity = fieldSet.reduce<ClassType<Record<string, any>>>(
    (prevEntity, key) => {
      const next = getRelationFieldInfo(
        repo(prevEntity as any).metadata.fields.find(key),
      );

      if (!next || !next.toEntity)
        throw new Error(
          `${key} is not a relation for ${repo(prevEntity as any).metadata.key}`,
        );

      return next.toEntity;
    },
    ctx.getEntity() as any,
  );

  const fieldMetadata = repo(finalEntity).metadata.fields.find(finalField);

  if(!fieldMetadata) throw new Error(`Field "${field}" not found in entity "${repo(finalEntity).metadata.key}"`);

  const finalCaption =
    typeof caption === "function"
      ? () => caption(fieldMetadata)
      : (caption ?? fieldMetadata.caption);

  const isARelation = field.includes(".");

  const { meta, ...rest } = override;

  return {
    id: field,
    accessorKey: field,
    include,
    enableResizing,
    ...resolveColumnResizeLimits({ minSize, maxSize, size, enableResizing }),
    cell: (info) => {
      const data = valueConverter
        ? valueConverter(info.row.original, info)
        : fieldSet.reduce((acc, key) => {
            if (Array.isArray(acc)) {
              throw new Error(
                `Must provide \`cell\` OR \`valueConverter\` properties for 'Column' when data is an array of arrays (meaning that the cell value is an array)`,
              );
            }
            return (acc as Obj)?.[key];
          }, info.row.original);

      const infoToShow =
        data !== null && data !== undefined
          ? typeof data === "string" || typeof data === "number"
            ? data
            : (fieldMetadata?.displayValue?.(data as Obj) ?? data)
          : "מידע אינו קיים";

      return <DefaultCell infoToShow={infoToShow} />;
    },
    enableSorting: !isARelation,
    meta: {
      field: fieldMetadata as FieldMetadata<unknown, Entity>,
      caption: finalCaption,
      ...meta,
    },
    ...rest,
    enablePinning: false,
    header: (headerContext) => {
      if (finalCaption === undefined) {
        console.error(
          `No default caption found for field "${field}" and none was provided`,
        );
      }
      return (
        <HyperTableHeader
          headerContext={headerContext}
          caption={finalCaption ?? ""}
        />
      );
    },
  };
}

export function mergeIncludes<T>(includes: Include<T>[]): Include<T> {
  return includes.reduce((acc, obj) => mergeDeep(acc, obj), {});

  function mergeDeep(a: any, b: any): any {
    if (
      typeof a !== "object" ||
      typeof b !== "object" ||
      a === null ||
      b === null
    ) {
      return b; // Base case
    }

    const result: any = { ...a };

    for (const key of Object.keys(b)) {
      if (key in a) {
        if (Array.isArray(a[key]) && Array.isArray(b[key])) {
          // Handle arrays like $or, $and
          result[key] = [...a[key], ...b[key]];
        } else if (typeof a[key] === "object" && typeof b[key] === "object") {
          result[key] = mergeDeep(a[key], b[key]);
        } else {
          result[key] = b[key];
        }
      } else {
        result[key] = b[key];
      }
    }

    return result;
  }
}

export function hasReachedScrollThreshold(el: Element, threshold: number) {
  const reachedBottom =
    el.scrollHeight - el.clientHeight - el.scrollTop <= threshold;
  return reachedBottom;
}
/**
 * ### Go to the [DOCUMENTATION](https://github.com/WEME-community/WEME/blob/WE-241-multiple-organizations/shared/frontend/hyper-table/HyperTable_README.md) - For more docs and examples.
 *
 * The `useCreateHyperTable` hook is the primary entry point for using the `HyperTable` component.
 * It provides a declarative API for building entity-driven tables with advanced features like filtering,
 * sorting, pagination, and dynamic column management.
 *
 * @template Entity - The type of the entity that defines the table's structure and behavior.
 * @param entity - The entity class that defines the table's structure and behavior.
 * This should be a class decorated with `remult` metadata.
 *
 * @returns An object containing the following components:
 *
 * - **`HyperTable`**: The main table component that manages state, data fetching, and rendering.
 * - **`Toolbar`**: A container for filters and actions displayed above the table.
 * - **`Column`**: A component for defining entity-based columns.
 * - **`NonEntityColumn`**: A component for defining custom columns not tied to an entity field.
 * - **`EntityFilter`**: A filter tied to a specific entity field.
 * - **`CustomFilter`**: A fully customizable filter component.
 * - **`SelectColumn`**: A column for row selection.
 * - **`context`**: A mutable reference to the table's context for advanced use cases.
 *
 * @example
 * // Basic Example
 * import React from "react";
 * import { User } from "@entities";
 * import { useCreateHyperTable } from "hyper-table";
 *
 * export default function App() {
 *   const { HyperTable, Column, Toolbar, EntityFilter } = useCreateHyperTable(User);
 *
 *   return (
 *     <HyperTable tableId="users-table" title="Users">
 *       <Toolbar>
 *         <EntityFilter fieldName="name">Name Filter</EntityFilter>
 *         <EntityFilter fieldName="email">Email Filter</EntityFilter>
 *       </Toolbar>
 *       <Column field="name">Name</Column>
 *       <Column field="email">Email</Column>
 *     </HyperTable>
 *   );
 * }
 */
export function useCreateHyperTable<Entity>(entity: ClassType<Entity>) {
  const ctxRef = useHyperTableContext<Entity>();

  const main = useMemo<HyperTableDef<Entity>>(() => {
    const main = function (
      props: Omit<HyperTableProps<Entity>, "entity" | "ctx">,
    ) {
      return <HyperTable {...props} entity={entity} ctx={ctxRef} />;
    };
    Object.assign<HyperTableDefMethod<Entity>, HyperTableDefValues<Entity>>(
      main,
      {
        HyperTable: main as HyperTableDef<Entity>,
        Toolbar: HyperTableToolbar,
        Column: ColumnComponent,
        NonEntityColumn,
        context: ctxRef,
        CustomFilter: HyperTableCustomFilter,
        EntityFilter: HyperTableEntityFilter,
        SelectColumn: HyperTableSelectRowColumn,
        ActionsBar,
        MenuColumn: HyperTableMenuActionsColumn,
        ExpandColumn: HyperTableExpandColumn,
      },
    );

    return main as HyperTableDef<Entity>;
  }, [entity, ctxRef]);

  return main;
}

export async function isValidEntityFilter<Entity>(
  entity: ClassType<Entity>,
  filter: EntityFilter<Entity>,
) {
  try {
    await SqlDatabase.filterToRaw(repo(entity), filter);
    return Object.keys(filter).length > 0;
  } catch {
    return false;
  }
}

export function useColumnFilters<Entity>(
  storageKey: string,
  entity: ClassType<Entity>,
) {
  const [loading, setLoading] = useState(true);
  const [columnFilters, columnFilterActions] = useArrayState<
    ActiveFilter<Entity>
  >([], {});

  const latestRequest = useRef<number>(0); // Track the latest async request

  useEffect(() => {
    setLoading(true);
    const requestId = ++latestRequest.current;

    (async () => {
      const storedData = getStoredValue<ActiveFilter<Entity>[]>(
        sessionStorage,
        storageKey,
        [],
        (f) =>
          Array.isArray(f) &&
          f.every(
            (v) =>
              typeof v === "object" &&
              v &&
              "id" in v &&
              "value" in v &&
              "saveInSessionStorage" in v &&
              typeof v.saveInSessionStorage === "boolean" &&
              "meta" in v &&
              typeof v.meta === "object" &&
              v.meta,
          ),
      );

      const validResults = await Promise.all(
        storedData.map((v) => isValidEntityFilter(entity, v.value)),
      );

      validResults.forEach((r, i) => {
        if (!r) {
          validResults.splice(i, 1);
        }
      });

      if (latestRequest.current === requestId) {
        columnFilterActions.set(storedData);
        setLoading(false);
      }
    })();

    return () => {
      latestRequest.current++;
    };
  }, [storageKey, entity, columnFilterActions.set]);

  useSaveToStorage(
    columnFilters.filter((v) => v.saveInSessionStorage),
    sessionStorage,
    storageKey,
    !loading,
  );

  return {
    columnFilters,
    columnFilterActions,
    loading,
    columnFiltersEnsured: !loading,
  };
}
